import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import sesLogo from '../../assets/SES Logo.png';

const BRAND = {
  cyan: '#00AEEF',
  magenta: '#EC008C',
  red: '#ED1C24',
};

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About Us' },
  { to: '/security', label: 'Security' },
  { to: '/contact', label: 'Contact' },
];

const CONTACT_ITEMS = [
  {
    label: 'Email',
    value: 'info@sespl.in',
    href: 'mailto:info@sespl.in',
    color: BRAND.cyan,
    icon: (
      <>
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </>
    ),
  },
  {
    label: 'Phone',
    value: '+91 97028 50777',
    href: 'tel:+91 97028 50777',
    color: BRAND.magenta,
    icon: <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />,
  },
  {
    label: 'WhatsApp',
    value: 'Chat with us',
    href: 'https://wa.me/919702850777',
    color: BRAND.red,
    icon: (
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    ),
  },
];

function ContactCard({ item }) {
  return (
    <a
      href={item.href}
      target={item.href.startsWith('http') ? '_blank' : undefined}
      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
      className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-slate-300"
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
        style={{ background: `${item.color}14`, color: item.color }}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {item.icon}
        </svg>
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{item.label}</p>
        <p className="text-sm font-semibold text-slate-800 break-words group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#00AEEF] group-hover:to-[#EC008C] transition-all">
          {item.value}
        </p>
      </div>
    </a>
  );
}

export default function Footer() {
  const [visible, setVisible] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <footer
      ref={footerRef}
      id="contact-section"
      className="relative overflow-hidden bg-white font-display"
    >
      {/* Light gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-white pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#00AEEF]/8 via-[#EC008C]/5 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#00AEEF]/8 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-[#ED1C24]/6 blur-[120px] pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 ses-brand-gradient opacity-60" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-8">
        {/* Main grid */}
        <div
          className={`about-reveal grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 ${visible ? 'about-visible' : ''}`}
        >
          {/* Brand column */}
          <div className="lg:col-span-5 space-y-6">
            <Link to="/" className="inline-flex items-center gap-3 group">
              <div className="relative rounded-2xl p-[2px] ses-brand-gradient">
                <div className="rounded-[0.9rem] bg-white p-1.5">
                  <img src={sesLogo} alt="SES" className="h-12 w-12 object-contain" />
                </div>
              </div>
              <div>
                <p className="text-base font-extrabold text-slate-900 leading-tight">
                  Sai Enforcement And Security Pvt. Ltd.
                </p>
                <p className="text-[10px] font-bold uppercase tracking-[0.15em] ses-brand-text mt-0.5">
                  Authorized MHADA Agency
                </p>
              </div>
            </Link>

            <p className="text-sm text-slate-500 leading-relaxed max-w-sm">
              Your trusted partner for government-approved MHADA homes in Kalyan. We connect
              families with affordable 1 BHK flats, transparent pricing, and full loan assistance.
            </p>

            <div className="flex flex-wrap gap-2">
              {['100% Authorized', '90% Loan Help', 'Kalyan MHADA'].map((tag, i) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border"
                  style={{
                    color: [BRAND.cyan, BRAND.magenta, BRAND.red][i],
                    borderColor: `${[BRAND.cyan, BRAND.magenta, BRAND.red][i]}33`,
                    background: `${[BRAND.cyan, BRAND.magenta, BRAND.red][i]}0d`,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div
            className={`about-reveal lg:col-span-3 ${visible ? 'about-visible' : ''}`}
            style={{ transitionDelay: '120ms' }}
          >
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {QUICK_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="nav-link-underline relative inline-block text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact cards */}
          <div
            className={`about-reveal lg:col-span-4 space-y-3 ${visible ? 'about-visible' : ''}`}
            style={{ transitionDelay: '220ms' }}
          >
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-5">
              Contact Us
            </h4>
            {CONTACT_ITEMS.map((item) => (
              <ContactCard key={item.label} item={item} />
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div
          className={`about-reveal mt-14 rounded-3xl p-[2px] ses-brand-gradient ${visible ? 'about-visible' : ''}`}
          style={{ transitionDelay: '350ms' }}
        >
          <div className="rounded-[1.35rem] bg-white/90 backdrop-blur-sm px-5 py-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
            <p className="text-center sm:text-left">
              © {new Date().getFullYear()} Sai Enforcement And Security Pvt. Ltd. All rights reserved.
            </p>
            <p className="text-center sm:text-right">
              Designed by{' '}
              <a
                href="https://100billiontech.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold ses-brand-text hover:opacity-80 transition-opacity"
              >
                100billion Tech
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
